import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import Input from "../../Shared/Input";
import FormFooter from "../../Shared/FormFooter";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import AlertRed from "../../Shared/AlertRed";
import PasswordStrengthIndicator from "../../Shared/PasswordStrengthIndicator";
import { updateAccountSettings } from "../../../api/settingsApi";
import { getPasswordValidationRules } from "../../../utils/passwordValidation";

const ChangePasswordForm = () => {
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const newPassword = watch("newPassword");

  // Update mutation
  const { mutate: updateSettings, isPending: isUpdating } = useMutation({
    mutationFn: updateAccountSettings,
    onSuccess: () => {
      setError(null);
      setSuccess(true);
      reset();
      // You could add a success toast here
    },
    onError: (err) => {
      setError({
        title: "Error",
        message:
          err.response?.data?.error?.message || "Failed to update password",
      });
    },
  });

  const onSubmit = async (data) => {
    // Don't save if nothing has changed
    if (!isDirty) {
      return;
    }

    setError(null);
    setSuccess(false);

    // Only send password-related fields
    const passwordData = {
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    };

    updateSettings(passwordData);
  };

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
        <div className="col-span-full">
          <Input
            id="currentPassword"
            name="currentPassword"
            label="Current password"
            type="password"
            errormessage={errors?.currentPassword?.message}
            {...register("currentPassword", {
              required: "Current password is required",
            })}
          />
        </div>

        <div className="col-span-full">
          <Input
            id="newPassword"
            name="newPassword"
            label="New password"
            type="password"
            errormessage={errors?.newPassword?.message}
            {...register("newPassword", getPasswordValidationRules())}
          />
          <PasswordStrengthIndicator password={newPassword} />
        </div>

        <div className="col-span-full">
          <Input
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm password"
            type="password"
            errormessage={errors?.confirmPassword?.message}
            {...register("confirmPassword", {
              required: "Please confirm your password",
              validate: (value) =>
                value === newPassword || "Passwords do not match",
            })}
          />
        </div>
      </div>
      <FormFooter
        primaryCaption="Save"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />
      {error ? (
        <div className="mt-4">
          <AlertRed title={error.title} message={error.message} />
        </div>
      ) : null}
      {success && (
        <div className="mt-4">
          <div className="rounded-md bg-signal-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-accent-700">
                  Password updated successfully
                </h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </TabFormWrapper>
  );
};

export default ChangePasswordForm;
